import {FC, useMemo, useState} from 'react';
import styled from 'styled-components';
import {useLocation, useParams} from 'wouter';

import {removeUndefined} from '@shared/lib/utils/type_utils';

import {useResponsive} from '@shared_web/breakpoints';
import {UnthemedNavLink} from '@shared_web/components/core/button';
import {SvgIcon} from '@shared_web/components/core/svg_icon';
import {Flex} from '@shared_web/components/core2/flex';
import {icon_arrow_left_line} from '@shared_web/components/icons/icon_arrow_left_line';

import {translate} from '@mavismart_frontend/components/translate';
import {SearchResultContextProvider} from '@mavismart_frontend/contexts/search_result_context';
import {DesktopTemplate} from '@mavismart_frontend/pages/desktop_template';
import {DoorTabs} from '@mavismart_frontend/pages/door_tabs';
import {
  AdvancedSearchForm,
  AdvancedSearchResultsData,
} from '@mavismart_frontend/pages/tabs/advanced_search_form';
import {AdvancedSearchResults} from '@mavismart_frontend/pages/tabs/advanced_search_results';
import {getUserLanguage, getUserLocale, useAccount} from '@mavismart_frontend/stores/account_store';

// Unified desktop page that keeps side lists mounted across route changes to preserve scroll
export const DesktopSearch: FC = () => {
  const {isDesktop} = useResponsive();
  const [_location, navigate] = useLocation();
  const account = useAccount();
  const lang = getUserLanguage(account);
  const locale = getUserLocale(account);
  const {doorId} = useParams<{doorId?: string}>();

  const [results, setResults] = useState<AdvancedSearchResultsData | undefined>(undefined);

  const {columns, content} = useMemo(() => {
    const content = doorId ? <DoorTabs doorId={doorId} baseUrl={`/search/${doorId}`} /> : <></>;

    const searchForm = (
      <AdvancedSearchForm
        key="search-form"
        title={<AdvancedSearchTitle>{translate('search', lang)}</AdvancedSearchTitle>}
        expandedOnMount={true}
        onResults={setResults}
      />
    );

    const resultsList = results ? <AdvancedSearchResults key="results-list" /> : undefined;

    if (!isDesktop && resultsList && doorId) {
      // TODO: Switching to the "tablet" UI is resetting the form because the component is unmounted
      return {
        columns: [
          <Flex key="results-list-with-back-button" $column>
            <UnthemedNavLink to={'/search'} expand>
              <Flex $width="100%" $gap="l" $padding="m" $align="center">
                <SvgIcon icon={icon_arrow_left_line} size={16} />
                {translate('back_to_search', lang).toLocaleUpperCase(locale)}
              </Flex>
            </UnthemedNavLink>
            {resultsList}
          </Flex>,
        ],
        content,
      };
    }
    return {columns: removeUndefined([searchForm, resultsList]), content};
  }, [lang, results, doorId, isDesktop, locale]);

  if (!results && doorId) {
    navigate('/search');
  }

  return (
    <SearchResultContextProvider value={results}>
      <DesktopTemplate columns={columns} content={content} />
    </SearchResultContextProvider>
  );
};

DesktopSearch.displayName = 'DesktopSearch';

const AdvancedSearchTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
`;
